import React, { useContext, useEffect, useState } from 'react';
import { Alert, Button, Col, Form, Row, Select } from 'antd';
import { useHistory } from 'react-router-dom';
import { PrimaryButton } from '../../Commons';
import { StepContext, WorkflowContext } from '../../Commons/Context';
import { api } from '../../Http';
import { delegateSaleForce } from '../../Http/salesforce/delegate';
import './style.scss';

const RETURN_PATH = 'EmailStatus';

const EmailInvoiceForm = () => {
  const { id, accountAuthorized } = useContext(StepContext);
  const { SObject, sampleData, mappings, setMappings, workflowId } = useContext(WorkflowContext);
  const [form] = Form.useForm();
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [metadataError, setMetadataError] = useState('');
  const [retry, setRetry] = useState(0);
  const [saved, setSaved] = useState(false);
  const history = useHistory();
  const objectUrl = SObject?.urls?.describe;
  const ready = Boolean(SObject?.name && sampleData?.Id && accountAuthorized);

  useEffect(() => {
    const current = (mappings || []).filter(mapping => String(mapping.stepId) === String(id));
    form.setFieldsValue({
      invoiceField: current.find(mapping => mapping.type === 'TRIG' && mapping.field === 'InvoiceID')?.value,
      returnField: current.find(mapping => mapping.type === 'ACT' && mapping.value === RETURN_PATH)?.field,
    });
  }, [form, id, mappings]);

  useEffect(() => {
    let active = true;
    setFields([]);
    if (!objectUrl) return;
    setLoading(true);
    setMetadataError('');
    delegateSaleForce({ uri: objectUrl }).then(response => {
      if (response.code !== 0 || !Array.isArray(response.data?.fields)) {
        throw new Error(response.message || 'Could not load Salesforce fields.');
      }
      if (active) setFields(response.data.fields);
    }).catch(error => {
      if (active) setMetadataError(error.message || 'Could not load Salesforce fields.');
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [objectUrl, retry]);

  const sourceFields = fields.filter(field =>
    Object.prototype.hasOwnProperty.call(sampleData || {}, field.name) &&
    ['string', 'id', 'reference'].includes(field.type));
  const destinationFields = fields.filter(field => field.updateable &&
    ['string', 'textarea'].includes(field.type));

  const save = async ({ invoiceField, returnField }) => {
    if (!ready || saving || saved) return;
    setSaving(true);
    setError('');
    const nextMappings = [
      { stepId: id, type: 'TRIG', field: 'InvoiceID', value: invoiceField, valueStepOrder: 1 },
      { stepId: id, type: 'ACT', field: returnField, value: RETURN_PATH, valueStepOrder: 1 },
    ];
    try {
      const response = await api.post('/api/v1/workflows/mappings', {
        mappings: nextMappings, sObject: SObject.name, workflowId,
      });
      if (response.data.code !== 0) throw new Error(response.data.message || 'Could not save the workflow.');
      setMappings(nextMappings);
      setSaved(true);
    } catch (error) {
      setError(error.data?.message || error.message || 'Could not save the workflow. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Row className='padded xinvform'>
      <div className='title'>Email an Invoice</div>
      <Col span={24}>
        <p>Email an existing Xero invoice and save the sending result to the originating Salesforce record.</p>
        <p className='hint'>Uses your connected Xero organisation and the invoice contact recipients configured in Xero. Saving configures this action; email is sent when the workflow runs.</p>
        {!ready && <Alert type='info' showIcon message='Choose your accounts, select a Salesforce object, and test the Salesforce trigger before mapping fields.' />}
        {ready && !objectUrl && <Alert type='warning' showIcon message='Select the Salesforce object again to load its field details.' />}
        {metadataError && <Alert type='error' showIcon message={metadataError}
          action={<Button onClick={() => setRetry(value => value + 1)}>Retry</Button>} />}
        {error && <Alert type='error' showIcon message={error} />}
        {saved ? <>
          <Alert type='success' showIcon message='Workflow mappings saved.'
            description='The workflow will request the invoice email from Xero and save Success or Unable to Send to Salesforce when it runs.' />
          <PrimaryButton className='btn-long' size='large' onClick={() => history.push('/system/workflows')}>Done</PrimaryButton>
        </> : <Form form={form} layout='vertical' onFinish={save} scrollToFirstError
          disabled={!ready || loading || saving || Boolean(metadataError)}>
          <Row justify='center'>
            <Col span={22}>
              <Form.Item name='invoiceField' label='Xero Invoice ID' rules={[
                { required: true, message: 'Select the Salesforce field containing the Xero Invoice ID.' },
                { validator: (_, value) => !value || sourceFields.some(field => field.name === value)
                  ? Promise.resolve() : Promise.reject(new Error('Select an available Salesforce source field.')) },
              ]} extra='Choose the field containing the Xero invoice UUID, for example Xero_Id__c.'>
                <Select className='rounded' showSearch allowClear loading={loading} optionFilterProp='label'
                  placeholder='Select a Salesforce field' options={sourceFields.map(field => ({
                    value: field.name, label: `${field.label} (${field.name}): ${sampleData?.[field.name] ?? '(empty)'}`,
                  }))} />
              </Form.Item>
              <Form.Item name='returnField' label='Save email status to' dependencies={['invoiceField']} rules={[
                { required: true, message: 'Select the Salesforce text field that will store the email status.' },
                { validator: (_, value) => !value || (destinationFields.some(field => field.name === value) && value !== form.getFieldValue('invoiceField'))
                  ? Promise.resolve() : Promise.reject(new Error('Select a writable text field different from the invoice ID field.')) },
              ]} extra='Stores Success when Xero accepts the request, or Unable to Send when the request fails. Success does not confirm delivery.'>
                <Select className='rounded' showSearch allowClear loading={loading} optionFilterProp='label'
                  placeholder='Select a Salesforce destination field' options={destinationFields.map(field => ({
                    value: field.name, label: `${field.label} (${field.name})`,
                  }))} />
              </Form.Item>
            </Col>
          </Row>
          <PrimaryButton className='btn-long' size='large' htmlType='submit' loading={saving}
            disabled={!ready || loading || !fields.length || Boolean(metadataError)}>Save Workflow</PrimaryButton>
        </Form>}
      </Col>
    </Row>
  );
};

export default EmailInvoiceForm;
